// src/features/equipment/hooks/useEquipmentFolder.js
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFolders } from '../../files/hooks/useFolders';

const EQUIPMENT_FOLDER_NAME = 'i18n-files:i18n_folder_names.equipment';

/** Folder name used for one piece of equipment under the Equipment folder. */
export const equipmentFolderName = (equipment) =>
  equipment?.room?.name ? `${equipment.room.name} - ${equipment.name}` : equipment?.name;

/**
 * Find the Equipment meta folder at root, then the subfolder named after this
 * specific equipment item. Returns { folderId, breadcrumbs } for use as an
 * upload target or SelectFileDialog starting point, or null if anything fails
 * (the dialog then just opens at root).
 *
 * With `{ create: true }` (uploading) missing folders are created. Without it
 * (just browsing) nothing is created: if the equipment has no folder yet the
 * Equipment folder is returned instead, so opening a dialog doesn't leave empty
 * folders behind.
 */
export function useEquipmentFolder() {
  const { t } = useTranslation(['files']);
  const { getRootContents, createFolder, getFolderContents } = useFolders();

  return useCallback(async (equipmentName, { create = false } = {}) => {
    try {
      const rootResult = await getRootContents();
      if (!rootResult?.success) throw new Error('Failed to fetch root contents');

      const rootData = rootResult.data.data;
      let equipmentMetaFolderId;

      const existingMeta = rootData.folders?.find(f => f.folder_name === EQUIPMENT_FOLDER_NAME);
      if (existingMeta) {
        equipmentMetaFolderId = existingMeta.folder_id;
      } else if (create) {
        const createResult = await createFolder({
          folder_name: EQUIPMENT_FOLDER_NAME,
          description: 'System folder for equipment files organized by equipment name',
          parent_folder_id: null,
          is_system_folder: false,
          is_protected: false,
        });
        if (!createResult?.success) throw new Error('Failed to create Equipment folder');
        equipmentMetaFolderId = createResult.data.data.folder_id;
      } else {
        return null;
      }

      const metaCrumbs = [
        { id: null, name: t('files:root') },
        { id: equipmentMetaFolderId, name: t('files:i18n_folder_names.equipment') },
      ];

      // Find or create per-equipment subfolder
      const contentsResult = await getFolderContents(equipmentMetaFolderId);
      if (!contentsResult?.success) throw new Error('Failed to fetch Equipment folder contents');

      const subfolders = contentsResult.data.data?.subfolders || [];
      let itemFolderId;

      const existingItem = subfolders.find(f => f.folder_name === equipmentName);
      if (existingItem) {
        itemFolderId = existingItem.folder_id;
      } else if (create) {
        const createResult = await createFolder({
          folder_name: equipmentName,
          description: `Files for equipment: ${equipmentName}`,
          parent_folder_id: equipmentMetaFolderId,
          is_system_folder: false,
          is_protected: false,
        });
        if (!createResult?.success) throw new Error('Failed to create equipment item folder');
        itemFolderId = createResult.data.data.folder_id;
      } else {
        return { folderId: equipmentMetaFolderId, breadcrumbs: metaCrumbs };
      }

      return {
        folderId: itemFolderId,
        breadcrumbs: [...metaCrumbs, { id: itemFolderId, name: equipmentName }],
      };
    } catch (err) {
      console.error('Error ensuring equipment folder:', err);
      return null;
    }
  }, [getRootContents, createFolder, getFolderContents, t]);
}
